import {Injectable} from '@angular/core';
import {Observable, of, take, map, shareReplay, switchMap} from 'rxjs';

import {UserService} from '../services/shared/user.service';
import PermissionLevel from './enums/permissionLevel.enum';
import {FinancialPermissionLevel} from './enums/financialPermissionLevel.enum';
import SectionsAccess from './enums/sectionsAccess.enum';
import ContactsVisibilityPermission from './enums/contactsVisibilityPermission.enum';
import {UserPermissionDataService} from '../services/gp/user-permission-data.service';
import ViewAsInvestorPermission from './enums/view-as-investor-permission';
import { GeneralSettingsService } from '../services/shared/general-settings.service';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  constructor(
    private userService: UserService,
    private userPermissionDataService: UserPermissionDataService,
    private generalSettingsService: GeneralSettingsService
  ) {}

  get isCovercyUser$() {
    return this.userService.getClientDetails().pipe(
      map((clientDetails) =>
        clientDetails.isEmailVerified
        && this.generalSettingsService.IsCovercyDomain(clientDetails.email)));
  }

  /**
   * Check in the user has the required permissions for some operation
   * @param minimumPermissionLevel Minimum required permission level
   * @param minimumFinancialPermissionLevel Minimum required financial permission level
   * @param requiredSectionsAccess Required section access
   * @param requireContactVisibility Required contact visibility
   * @param minimumViewAsInvestorPermission Minimum view as investor permission
   * @return Observable of [has the required permission, failed for permission type]
   */
  public checkPermission(minimumPermissionLevel?: PermissionLevel, minimumFinancialPermissionLevel?: FinancialPermissionLevel,
                         requiredSectionsAccess?: SectionsAccess, requireContactVisibility?: boolean,
                         minimumViewAsInvestorPermission?: ViewAsInvestorPermission): Observable<[boolean, string]> {
    return this.userService.accountDetails$.pipe(
      map(accountDetails => {
        if (!accountDetails.isAgent) {
          return [true, undefined];
        }
        const userPermission = accountDetails.userPermission;
        if (!this.isSufficientPermissionLevel(userPermission.permissionLevel, minimumPermissionLevel)) {
          return [false, 'PermissionLevel'];
        }

        if (!this.isSufficientFinancialPermissionLevel(userPermission.financialPermission, minimumFinancialPermissionLevel)) {
          return [false, 'FinancialPermissionLevel'];
        }

        if (!!requiredSectionsAccess && !SectionsAccess.isOn(userPermission.allowedSections, SectionsAccess.All) &&
          !SectionsAccess.isOn(userPermission.allowedSections, requiredSectionsAccess)) { // && requiredSectionsAccess !== SectionsAccess.None // have no overlap with None
          return [false, 'SectionsAccess'];
        }

        if (!!requireContactVisibility && userPermission.contactsVisibility === ContactsVisibilityPermission.Hidden) {
          return [false, 'contactsVisibility'];
        }

        if (!this.isSufficientViewAsInvestorPermission(userPermission.viewAsInvestor, minimumViewAsInvestorPermission)) {
          return [false, 'viewAsInvestor'];
        }

        return [true, undefined];
      })
    );
  }

  private isSufficientPermissionLevel(userPermissionLevel: PermissionLevel, minimumPermissionLevel: PermissionLevel): boolean {
    switch (minimumPermissionLevel) {
      case PermissionLevel.None:
      case PermissionLevel.Readonly:
        return true;
      case PermissionLevel.Editor:
        return userPermissionLevel === PermissionLevel.Editor || userPermissionLevel === PermissionLevel.Admin;
      case PermissionLevel.Admin:
        return userPermissionLevel === PermissionLevel.Admin;
      default:
        return true;
    }
  }

  private isSufficientFinancialPermissionLevel(userFinancialPermission: FinancialPermissionLevel, minimumFinancialPermissionLevel: FinancialPermissionLevel) {
    switch (minimumFinancialPermissionLevel) {
      case FinancialPermissionLevel.None:
      case FinancialPermissionLevel.Hidden:
        return true;
      case FinancialPermissionLevel.NonSignatory:
        return userFinancialPermission === FinancialPermissionLevel.NonSignatory || userFinancialPermission === FinancialPermissionLevel.AuthorizedSignatory;
      case FinancialPermissionLevel.AuthorizedSignatory:
        return userFinancialPermission === FinancialPermissionLevel.AuthorizedSignatory;
      default:
        return true;
    }
  }

  private isSufficientViewAsInvestorPermission(userViewAsInvestorPermission: ViewAsInvestorPermission, minimumViewAsInvestorPermission: ViewAsInvestorPermission) {
    switch (minimumViewAsInvestorPermission) {
      case ViewAsInvestorPermission.Forbidden:
        return userViewAsInvestorPermission === ViewAsInvestorPermission.Forbidden;
      case ViewAsInvestorPermission.Allowed:
        return userViewAsInvestorPermission === ViewAsInvestorPermission.Allowed;
      default:
        return true;
    }
  }

  get allowInvestorName$(): Observable<boolean> {
    return this.userService.accountDetails$.pipe(
      map(accountDetails => {
        if (!accountDetails.isAgent) {
          return true;
        }
        const userPermission = accountDetails.userPermission;
        return userPermission?.contactsVisibility === ContactsVisibilityPermission.Visible;
      }),
      shareReplay(1)
    );
  }

  get isAllContactsVisible$(): Observable<boolean> {
    return this.userService.accountDetails$.pipe(
      switchMap(accountDetails => {
        if (!accountDetails.isAgent) {
          return of(true);
        }
        return this.userPermissionDataService.isAllContactsVisible();
      }),
      shareReplay(1)
    );
  }

  get showBankInfo$(): Observable<boolean> {
    return this.userService.accountDetails$.pipe(
      map(accountDetails => {
        return (
          accountDetails.userPermission?.financialPermission === FinancialPermissionLevel.AuthorizedSignatory
          || accountDetails.userPermission?.financialPermission === FinancialPermissionLevel.NonSignatory
        );
      }),
      shareReplay(1)
    );
  }

  get haveAuthorizedSignatory$(): Observable<boolean> {
    return this.userService.accountDetails$.pipe(
      map(accountDetails => {
        return (
          !accountDetails.isAgent
          || accountDetails.userPermission?.financialPermission === FinancialPermissionLevel.AuthorizedSignatory
        );
      }),
      shareReplay(1)
    );
  }

  get nonAdminViewBanking$(): Observable<boolean> {
    return this.userService.accountDetails$.pipe(
      map(accountDetails =>
        accountDetails.userPermission?.permissionLevel !== PermissionLevel.Admin
        &&
        accountDetails.userPermission?.financialPermission === FinancialPermissionLevel.NonSignatory
      ),
      shareReplay(1)
    );
  }

  get nonAdminHideBanking$(): Observable<boolean> {
    return this.userService.accountDetails$.pipe(
      map(accountDetails =>
        accountDetails.userPermission?.permissionLevel !== PermissionLevel.Admin
        &&
        accountDetails.userPermission?.financialPermission === FinancialPermissionLevel.Hidden
      ),
      shareReplay(1)
    );
  }
}
